import { useState, useEffect, useRef, Fragment } from "react";
import "./styles.sass";
import useHandler from "./handler";
import assets from "../../assets";
import { useApi } from "../../hooks/use-api";
import { apiTeacherStudentSubtopic } from "../../utils/api-routes";
// Modals
import { StudentAiSummaryModal } from "./student-ai-modal";
import { TestQuestionDetailsModal } from "./test-question-modal";

const formatLabel = (value) => {
  return value
    ?.split("_")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    ?.join(" ");
};

const getMockConcepts = (chapterItem) => {
  if (chapterItem?.concepts && Array.isArray(chapterItem.concepts) && chapterItem.concepts.length > 0) {
    return chapterItem.concepts;
  }
  if (chapterItem?.sub_topics && Array.isArray(chapterItem.sub_topics) && chapterItem.sub_topics.length > 0) {
    return chapterItem.sub_topics;
  }

  const rawName = chapterItem?.chapter_name || "";
  const nameLower = rawName.toLowerCase();

  if (nameLower.includes("cell")) {
    return [
      { concept_name: "Cell Organelles", attempts: 4, attempted_questions: 4, not_attempted_questions: 20, correct_answers: 20, incorrect_answers: 20, score: "66.6%" },
      { concept_name: "Plasma Membrane", attempts: 4, attempted_questions: 6, not_attempted_questions: 20, correct_answers: 20, incorrect_answers: 20, score: "66.6%" },
      { concept_name: "Cell Wall", attempts: 4, attempted_questions: 8, not_attempted_questions: 20, correct_answers: 20, incorrect_answers: 20, score: "66.6%" },
    ];
  }

  if (nameLower.includes("state") || nameLower.includes("matter")) {
    return [
      { concept_name: "Intermolecular Forces", attempts: chapterItem?.attempts ?? 1, attempted_questions: 8, not_attempted_questions: 0, correct_answers: 4, incorrect_answers: 4, score: "50%" },
      { concept_name: "Gas Laws & Kinetic Theory", attempts: chapterItem?.attempts ?? 1, attempted_questions: 7, not_attempted_questions: 0, correct_answers: 3, incorrect_answers: 4, score: "42.8%" },
      { concept_name: "Liquid State & Surface Tension", attempts: chapterItem?.attempts ?? 1, attempted_questions: 5, not_attempted_questions: 0, correct_answers: 3, incorrect_answers: 2, score: "60%" },
    ];
  }

  if (nameLower.includes("solution")) {
    return [
      { concept_name: "Types of Solutions & Solubility", attempts: chapterItem?.attempts ?? 1, attempted_questions: 2, not_attempted_questions: 0, correct_answers: 2, incorrect_answers: 0, score: "100%" },
      { concept_name: "Colligative Properties", attempts: chapterItem?.attempts ?? 1, attempted_questions: 2, not_attempted_questions: 0, correct_answers: 1, incorrect_answers: 1, score: "50%" },
      { concept_name: "Raoult's Law & Ideal Solutions", attempts: chapterItem?.attempts ?? 1, attempted_questions: 1, not_attempted_questions: 0, correct_answers: 1, incorrect_answers: 0, score: "100%" },
    ];
  }

  if (nameLower.includes("hydrocarbon")) {
    return [
      { concept_name: "Alkanes & Conformations", attempts: chapterItem?.attempts ?? 2, attempted_questions: 4, not_attempted_questions: 0, correct_answers: 2, incorrect_answers: 2, score: "50%" },
      { concept_name: "Alkenes & Electrophilic Addition", attempts: chapterItem?.attempts ?? 2, attempted_questions: 3, not_attempted_questions: 0, correct_answers: 1, incorrect_answers: 2, score: "33.3%" },
      { concept_name: "Aromatic Hydrocarbons & Resonance", attempts: chapterItem?.attempts ?? 2, attempted_questions: 3, not_attempted_questions: 0, correct_answers: 1, incorrect_answers: 2, score: "33.3%" },
    ];
  }

  const cleanName = rawName.replace(/^Chapter\s*:\s*\d+[-:]?\s*/i, "").trim() || "Concept";
  return [
    { concept_name: `${cleanName} - Fundamentals`, attempts: chapterItem?.attempts ?? 1, attempted_questions: 8, not_attempted_questions: 0, correct_answers: 6, incorrect_answers: 2, score: "75%" },
    { concept_name: `${cleanName} - Core Principles`, attempts: chapterItem?.attempts ?? 1, attempted_questions: 6, not_attempted_questions: 0, correct_answers: 4, incorrect_answers: 2, score: "66.6%" },
    { concept_name: `${cleanName} - Applications & Practice`, attempts: chapterItem?.attempts ?? 1, attempted_questions: 6, not_attempted_questions: 0, correct_answers: 5, incorrect_answers: 1, score: "83.3%" },
  ];
};

const formatAiSummaryText = (text) => {
  if (!text) return null;
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim().replace(/^[*•-]\s*/, ""))
    .filter(Boolean);

  return (
    <ul className="test_ai_bullet_list">
      {lines.map((line, idx) => {
        const parts = line.split(/(\*\*.*?\*\*)/g);
        return (
          <li key={idx}>
            {parts.map((part, pIdx) => {
              if (part.startsWith("**") && part.endsWith("**")) {
                return <strong key={pIdx}>{part.slice(2, -2)}</strong>;
              }
              return part;
            })}
          </li>
        );
      })}
    </ul>
  );
};

const ConfirmationModal = ({
  open,
  title,
  description,
  subDescription,
  onCancel,
  onConfirm,
}) => {
  if (!open) return null;

  return (
    <section className="teacher_confirmation_overlay">
      <section className="teacher_confirmation_modal">
        <section className="teacher_confirmation_header">
          <h3>{title}</h3>

          {/* <span className="teacher_close_btn" onClick={onCancel}>
            ✕
          </span> */}
        </section>

        <p className="teacher_warning_text">{description}</p>

        <p className="teacher_description">{subDescription}</p>

        <section className="teacher_confirmation_footer">
          <button className="teacher_cancel_btn" onClick={onCancel}>
            Cancel
          </button>

          <button className="teacher_confirm_btn" onClick={onConfirm}>
            Confirm
          </button>
        </section>
      </section>
    </section>
  );
};

const CustomFilterSelect = ({
  name,
  value,
  onChange,
  options,
  placeholder,
  searchable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = (options || []).filter((opt) => {
    const label = typeof opt === "string" ? opt : opt.label || opt.value || "";
    return label.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const selectedLabel = (() => {
    if (!value) return placeholder;
    const found = (options || []).find((opt) => {
      const val = typeof opt === "string" ? opt : opt.value;
      return val === value;
    });
    if (!found) return value;
    return typeof found === "string" ? found : found.label;
  })();

  const handleSelect = (val) => {
    onChange({ target: { name, value: val } });
    setIsOpen(false);
    setSearchQuery("");
  };

  return (
    <div
      className={`custom_filter_select_wrapper ${isOpen ? "is-open" : ""}`}
      ref={dropdownRef}
    >
      <div
        className={`custom_filter_select_trigger ${!value ? "is-placeholder" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="trigger_label">{selectedLabel}</span>
        <svg
          className={`trigger_chevron ${isOpen ? "rotate" : ""}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6B7280"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>

      {isOpen && (
        <div className="custom_filter_select_dropdown">
          {searchable && (
            <div className="custom_filter_search_box">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="custom_filter_options_list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, idx) => {
                const val = typeof opt === "string" ? opt : opt.value;
                const label = typeof opt === "string" ? opt : opt.label;
                const isSelected = val === value;

                return (
                  <div
                    key={idx}
                    className={`custom_filter_option_item ${
                      isSelected ? "selected" : ""
                    }`}
                    onClick={() => handleSelect(val)}
                  >
                    <span>{label}</span>
                  </div>
                );
              })
            ) : (
              <div className="custom_filter_no_options">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const FilterModalPopup = ({
  open,
  onClose,
  onApply,
  onClear,
  selectedExam,
  selectedClass,
  selectedTieres,
  selectedTopic,
  selectedChapter,
  examList,
  classList,
  topicList,
  chapterList,
  tierList,
  onChangeValue,
}) => {
  if (!open) return null;

  return (
    <section className="teacher_filter_overlay" onClick={onClose}>
      <section
        className="teacher_filter_modal"
        onClick={(e) => e.stopPropagation()}
      >
        <section className="teacher_filter_header">
          <h3>Filters</h3>

          <span onClick={onClose}>✕</span>
        </section>

        <section className="teacher_filter_grid">
          <section className="teacher_filter_item">
            <label>Exam</label>
            <CustomFilterSelect
              name="selectedExam"
              value={selectedExam}
              onChange={onChangeValue}
              options={examList}
              placeholder="All Exam"
              searchable={false}
            />
          </section>

          <section className="teacher_filter_item">
            <label>Class</label>
            <CustomFilterSelect
              name="selectedClass"
              value={selectedClass}
              onChange={onChangeValue}
              options={classList}
              placeholder="Select Class"
              searchable={false}
            />
          </section>

          <section className="teacher_filter_item">
            <label>Chapter</label>
            <CustomFilterSelect
              name="selectedChapter"
              value={selectedChapter}
              onChange={onChangeValue}
              options={chapterList}
              placeholder="Select Chapter"
              searchable={true}
            />
          </section>

          <section className="teacher_filter_item">
            <label>Topic</label>
            <CustomFilterSelect
              name="selectedTopic"
              value={selectedTopic}
              onChange={onChangeValue}
              options={topicList}
              placeholder="Select Topics"
              searchable={true}
            />
          </section>

          <section className="teacher_filter_item teacher_full_width">
            <label>Student Tier</label>
            <CustomFilterSelect
              name="selectedTieres"
              value={selectedTieres}
              onChange={onChangeValue}
              options={(tierList || []).map((t) => ({
                value: t,
                label: formatLabel(t),
              }))}
              placeholder="Select Tier"
              searchable={false}
            />
          </section>
        </section>

        <section className="teacher_filter_footer">
          <button className="teacher_clear_btn" onClick={onClear}>
            Clear All
          </button>

          <button className="teacher_apply_btn" onClick={onApply}>
            Apply Filters
          </button>
        </section>
      </section>
    </section>
  );
};

export const StudentPerformance = () => {
  const {
    openPopup,
    selectedStudent,
    totalPages,
    currentPage,
    startItem,
    endItem,
    currentStudents,
    boardData,
    weaknessDetails,
    showWeakness,
    loading,
    user,
    tiars,
    selectedTier,
    selectedStudents,
    testBased,
    showConfirm,
    moveTier,
    showFilter,
    filterDatas,
    openDropdown,
    dropdownRef,
    examList,
    classList,
    topicList,
    chapterList,
    tierList,
    performanceTable,
    newbreakDetails,
    breakdownLoading,
    setWeaknessDatails,
    setShowWeakness,
    setOpenPopup,
    setCurrentPage,
    getStudentLeaderBoardChapterBreakDown,
    getWeakneesAnalysis,
    teacherSendTipsToStudents,
    getStudentLeaderBoardDetails,
    setLoading,
    handleTierChange,
    handleSelectStudent,
    setSelectedStudents,
    setTestBased,
    setShowConfirm,
    setMoveTier,
    onMoveTier,
    setShowFilter,
    clearFilters,
    onChangeValue,
    setOpenDropdown,
    applyFilters,
    changeStudentTiers,
    getNewChapterBreakDown,
    setSelectedTier,
    getStudentPerformanceTable,
    testQuestionModalOpen,
    setTestQuestionModalOpen,
    testQuestionDetails,
    testQuestionLoading,
    selectedQuestionTest,
    getTestQuestionDetails,
    studentAiModalOpen,
    setStudentAiModalOpen,
    studentAiModalData,
    studentAiModalLoading,
    selectedAiTest,
    openStudentAiSummary,
    handleViewQuestionDetails,
  } = useHandler();

  const {
    selectedExam,
    selectedClass,
    selectedTopic,
    selectedTieres,
    selectedChapter,
  } = filterDatas;

  const { get } = useApi();
  const [expandedChapters, setExpandedChapters] = useState({});
  const [subTopicsMap, setSubTopicsMap] = useState({});
  const [subTopicsLoading, setSubTopicsLoading] = useState({});
  const [breakdownSearch, setBreakdownSearch] = useState("");
  const [breakdownPage, setBreakdownPage] = useState(1);
  const BREAKDOWN_PAGE_SIZE = 10;

  const toggleChapter = async (index, item) => {
    const isExpanding = !expandedChapters[index];

    setExpandedChapters((prev) => ({
      ...prev,
      [index]: isExpanding,
    }));

    const chapterName = item?.chapter_name || item?.chapter || item?.test_name || "";

    if (isExpanding && chapterName && !subTopicsMap[chapterName]) {
      const studentEmail =
        newbreakDetails?.summary?.email ||
        newbreakDetails?.email ||
        selectedStudent?.email ||
        "";

      if (!studentEmail) {
        return;
      }

      const currentSubject =
        item?.subject ||
        filterDatas?.selectedSubject ||
        user?.data?.subject ||
        user?.subject ||
        "Chemistry";

      const currentExam =
        item?.exam ||
        filterDatas?.selectedExam ||
        user?.data?.exam ||
        user?.exam ||
        "NEET";

      setSubTopicsLoading((prev) => ({ ...prev, [chapterName]: true }));

      try {
        let res = await get(
          apiTeacherStudentSubtopic(studentEmail, currentSubject, chapterName)
        );
        let dataObj = res?.data || res;
        let subTopics = Array.isArray(dataObj?.sub_topics)
          ? dataObj.sub_topics
          : Array.isArray(dataObj?.data?.sub_topics)
          ? dataObj.data.sub_topics
          : Array.isArray(dataObj?.data)
          ? dataObj.data
          : Array.isArray(dataObj)
          ? dataObj
          : [];

        if (!subTopics || subTopics.length === 0) {
          const rawUrl = `/api/teacher/student/${encodeURIComponent(studentEmail)}/weakness-details/subtopic`;
          const altRes = await get(rawUrl, {
            params: {
              subject: currentSubject,
              chapter: chapterName,
            },
          });
          const altDataObj = altRes?.data || altRes;
          const altSubTopics = Array.isArray(altDataObj?.sub_topics)
            ? altDataObj.sub_topics
            : Array.isArray(altDataObj?.data?.sub_topics)
            ? altDataObj.data.sub_topics
            : Array.isArray(altDataObj?.data)
            ? altDataObj.data
            : Array.isArray(altDataObj)
            ? altDataObj
            : [];

          if (altSubTopics && altSubTopics.length > 0) {
            subTopics = altSubTopics;
          }
        }

        setSubTopicsMap((prev) => ({
          ...prev,
          [chapterName]: subTopics,
        }));
      } catch (err) {
        console.error("Error fetching subtopics for chapter:", chapterName, err);
        setSubTopicsMap((prev) => ({
          ...prev,
          [chapterName]: getMockConcepts(item),
        }));
      } finally {
        setSubTopicsLoading((prev) => ({ ...prev, [chapterName]: false }));
      }
    }
  };

  const tierClass = {
    top_tier: "top-tier",
    mid_tier: "mid-tier",
    low_tier: "low-tier",
  };

  const isChapterBased =
    testBased === "chapter"
      ? newbreakDetails?.chapters || newbreakDetails?.tests || []
      : newbreakDetails?.tests || newbreakDetails?.chapters || [];

  const filteredBreakdown = (isChapterBased || []).filter((item) => {
    if (!breakdownSearch.trim()) return true;
    const name = (item?.test_name || item?.chapter_name || item?.chapter || "").toLowerCase();
    return name.includes(breakdownSearch.trim().toLowerCase());
  });

  const totalBreakdownPages = Math.max(1, Math.ceil(filteredBreakdown.length / BREAKDOWN_PAGE_SIZE));
  const pagedBreakdown = filteredBreakdown.slice(
    (breakdownPage - 1) * BREAKDOWN_PAGE_SIZE,
    breakdownPage * BREAKDOWN_PAGE_SIZE
  );

  return (
    <div className="student_performance">
      <section>
        <header className="student_teacher_header">
          <div className="student_header_left">
            <h1>Student Performance 👋</h1>
            <p>Overview of your student’s perforamce</p>
          </div>
          <div className="student_header_right">
            <div className="student_profile_brief">
              <div className="student_avatar">
                {user?.data?.name?.[0]?.toUpperCase()}
              </div>
              <div className="student_profile_info">
                <span className="student_name">{user?.data?.name}</span>
                <span className="student_role">{user?.data?.subject}</span>
              </div>
            </div>
          </div>
        </header>

        {selectedStudents.length === 0 && (
          <section className="teacher_student_header_btns">
            <section
              className="teacher_student_filter"
              onClick={() => setShowFilter(true)}
            >
              <img src={assets.ic_filter} alt="ic_filter" />
              <h4>Filters</h4>
            </section>
            <section
              className="teacher_student_refresh"
              onClick={() => {
                setLoading(true);
                setTimeout(() => {
                  setLoading(false);
                  getStudentPerformanceTable();
                }, 1000);
              }}
            >
              <img src={assets.ic_refetch_blue} alt="ic_refetch_blue" />

              <h4>Refresh</h4>
            </section>
          </section>
        )}

        {selectedStudents.length > 0 && (
          <section className="teacher_selected_main_header_container">
            <section className="teacher_selected_header_wrapper">
              <h4>{selectedStudents.length} students selected</h4>
              <h5 onClick={() => setSelectedStudents([])}>Clear Selection</h5>
            </section>
            <section className="teacher_selected_header_tier_wrapper">
              <h4>Categorize selected students</h4>
              <section className="teacher_header_tier_wrapper">
                <section
                  className="teacher_header_top_tier_container"
                  onClick={() => onMoveTier("top_tier")}
                >
                  <h4>Top Tier</h4>
                </section>
                <section
                  className="teacher_header_mid_tier_container"
                  onClick={() => onMoveTier("mid_tier")}
                >
                  <h4>Mid Tier</h4>
                </section>
                <section
                  className="teacher_header_low_tier_container"
                  onClick={() => onMoveTier("low_tier")}
                >
                  <h4>Low Tier</h4>
                </section>
              </section>
            </section>
          </section>
        )}

        {loading && (
          <section className="teacher_student_loading_wrapper">
            <section className="teacher_student_loading"></section>
          </section>
        )}

        <ConfirmationModal
          open={showConfirm}
          title={`Move to ${moveTier}?`}
          description={`Are you sure you want to move the selected ${selectedStudents.length} students to ${moveTier}?`}
          subDescription="Students will receive additional practice materials, personalized worksheets, and focused learning recommendations."
          onCancel={() => setShowConfirm(false)}
          onConfirm={async () => {
            // moveStudentsApi();
            await changeStudentTiers();
            setShowConfirm(false);
          }}
        />

        <FilterModalPopup
          open={showFilter}
          onClose={() => setShowFilter(false)}
          onClear={clearFilters}
          // onApply={applyFilters}
          selectedExam={selectedExam}
          selectedClass={selectedClass}
          selectedChapter={selectedChapter}
          selectedTopic={selectedTopic}
          selectedTieres={selectedTieres}
          examList={examList}
          topicList={topicList}
          chapterList={chapterList}
          classList={classList}
          tierList={tierList}
          onChangeValue={onChangeValue}
          onApply={applyFilters}
        />

        {!loading && (
          <div className="table_wrapper">
            <table>
              <thead>
                <tr>
                  <th>
                    {currentStudents?.length && (
                      <input
                        type="checkbox"
                        checked={
                          currentStudents?.length > 0 &&
                          currentStudents.every((item) =>
                            selectedStudents.includes(item?.email),
                          )
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStudents((prev) => [
                              ...new Set([
                                ...prev,
                                ...currentStudents.map((item) => item?.email),
                              ]),
                            ]);
                          } else {
                            const currentEmails = currentStudents.map(
                              (item) => item?.email,
                            );

                            setSelectedStudents((prev) =>
                              prev.filter(
                                (email) => !currentEmails.includes(email),
                              ),
                            );
                          }
                        }}
                        className="student_select"
                      />
                    )}
                  </th>
                  <th>Student Information</th>
                  <th>Test Attended</th>
                  <th>Student Tier</th>
                  <th>Score Obtained</th>
                  <th>Best Attempted Score</th>
                  <th>Average Score</th>
                  {/* <th>Highest Score (%)</th> */}
                  <th>Overall (%)</th>
                  {/* <th></th> */}
                  <th>Move Tier</th>
                </tr>
              </thead>

              <tbody>
                {currentStudents?.length > 0 ? (
                  currentStudents?.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(item?.email)}
                          onChange={() => handleSelectStudent(item?.email)}
                          className="student_select"
                        />
                      </td>
                      <td>
                        <div className="student_info">
                          {/* <div className="avatar">
                          {(
                            item?.name?.[0] ||
                            item?.user_mail?.[0] ||
                            "?"
                          ).toUpperCase()}
                        </div> */}

                          <div
                            className="list_table_email_wrapper"
                            onClick={() =>
                              // getStudentLeaderBoardChapterBreakDown(item?.email)
                              getNewChapterBreakDown(item?.email, testBased)
                            }
                          >
                            <h4>{item?.student_name || "-"}</h4>
                            <span>{item?.email}</span>
                          </div>
                        </div>
                      </td>

                      <td>{item?.tests_attended}</td>
                      <td>
                        <section
                          className={`teir_table_item ${
                            item?.student_tier === "top_tier"
                              ? "top-tier"
                              : item?.student_tier === "mid_tier"
                                ? "mid-tier"
                                : item?.student_tier === "low_tier"
                                  ? "low-tier"
                                  : ""
                          }`}
                        >
                          {formatLabel(item?.student_tier)}
                        </section>
                      </td>
                      <td>{item?.score_obtained ?? item?.total_marks_scored ?? "-"}</td>
                      <td>
                        {item?.best_attempt_score
                          ? String(item.best_attempt_score).replace(/\s*out\s*of\s*/gi, " / ")
                          : item?.max_score ?? "-"}
                      </td>

                      <td className="danger">{item?.average_score}</td>

                      {/* <td className="warning">{item.highest}%</td> */}

                      <td className="success">{item?.overall_percentage}%</td>

                      {/* <td>
                      <section
                        className="weakness_btn"
                        onClick={() =>
                          getStudentLeaderBoardChapterBreakDown(item?.user_mail)
                        }
                      >
                        Show Weakness
                      </section>
                    </td> */}
                      <td>
                        <section
                          className={`move_tier_wrapper ${
                            tierClass[
                              selectedTier[item?.email] || item?.student_tier
                            ] || ""
                          } ${openDropdown === item?.email ? "is-open" : ""}`}
                          ref={
                            openDropdown === item?.email ? dropdownRef : null
                          }
                        >
                          <section
                            className="move_tier_container"
                            onClick={() =>
                              setOpenDropdown(
                                openDropdown === item.email ? null : item.email,
                              )
                            }
                          >
                            <h5>
                              {formatLabel(
                                selectedTier[item.email] || item?.student_tier,
                              )}
                            </h5>
                            <svg
                              className={`tier_chevron ${openDropdown === item?.email ? "rotate" : ""}`}
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="m6 9 6 6 6-6"/>
                            </svg>
                          </section>
                          {openDropdown === item.email && (
                            <section className="tier_dropdown_wrapper">
                              {tiars.map(({ key }) => {
                                const isCurrentSelected =
                                  (selectedTier[item?.email] || item?.student_tier) === key;

                                return (
                                  <div
                                    className={`tier_dropdown_item ${isCurrentSelected ? "selected" : ""}`}
                                    key={key}
                                    onClick={() => {
                                      setOpenDropdown(null);
                                      setMoveTier(key);
                                      setSelectedStudents([item?.email]);
                                      setShowConfirm(true);
                                    }}
                                  >
                                    <span>{formatLabel(key)}</span>
                                  </div>
                                );
                              })}
                            </section>
                          )}
                        </section>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="no_data_found_table">
                      No Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="pagination_container">
              <div className="pagination_info">
                Showing {startItem} to {endItem} of {performanceTable?.length}{" "}
                students
              </div>

              <div className="pagination">
                <button
                  className="page_btn_wrapper"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  {"<"}
                </button>

                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`page_btn_wrapper ${currentPage === i + 1 ? "active" : ""} `}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  className="page_btn_wrapper"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  {">"}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {openPopup && (
        <div
          className="teacher_drawer_overlay"
          onClick={() => setOpenPopup(false)}
        >
          <div className="teacher_drawer" onClick={(e) => e.stopPropagation()}>
            <section className="teacher_drawer_heading_container">
              <h2>Student Details</h2>
              <section
                className="teacher_close_btn"
                onClick={() => {
                  setOpenPopup(false);
                  setWeaknessDatails();
                  setShowWeakness(false);
                }}
              >
                ×
              </section>
            </section>
            <section className="teacher_drawer_header">
              <section className="teacher_student_info">
                <section className="teacher_student_info_details">
                  <section className="teacher_avatar">
                    {newbreakDetails?.summary?.name?.[0]?.toUpperCase() || "-"}
                  </section>

                  <section className="teacher_drawer_name_wrapper">
                    <h4>{newbreakDetails?.summary?.name || "-"}</h4>

                    <span>{newbreakDetails?.summary?.email || ""}</span>
                  </section>
                </section>
                <div className="teacher_cards">
                  <section className="teahcer_wrapper_card">
                    <img src={assets.ic_test_attented} alt="ic_test_attented" />
                    <div className="teacher_card">
                      <span>Test Attended</span>
                      <h3>
                        {newbreakDetails?.summary?.test_attended ??
                          newbreakDetails?.summary?.tests_attended ??
                          "-"}
                      </h3>
                    </div>
                  </section>
                  <section className="teahcer_wrapper_card">
                    <img src={assets.ic_overall} alt="ic_overall" />
                    <div className="teacher_card">
                      <span>Overall Percentage</span>
                      <h3>
                        {newbreakDetails?.summary?.overall_percentage !== undefined &&
                        newbreakDetails?.summary?.overall_percentage !== null
                          ? `${newbreakDetails?.summary?.overall_percentage}%`
                          : "-"}
                      </h3>
                    </div>
                  </section>
                  <section className="teahcer_wrapper_card">
                    <img src={assets.ic_average} alt="ic_average" />
                    <div className="teacher_card">
                      <span>Average Marks</span>
                      <h3>
                        {newbreakDetails?.summary?.average_marks ??
                          newbreakDetails?.summary?.average_score ??
                          "-"}
                      </h3>
                    </div>
                  </section>
                </div>
              </section>
            </section>

            <div className="teacher_table_section">
              <h4>Performance Breakdown</h4>
              <div className="breakdown_toolbar">
                <section className="student_performance_wrapper">
                  <section className="student_performance_container">
                    <section
                      className={testBased === "chapter" ? "chapter_wise" : ""}
                      onClick={() => { setTestBased("chapter"); setBreakdownPage(1); setBreakdownSearch(""); }}
                    >
                      Chapter Wise
                    </section>
                    <section
                      className={testBased === "test" ? "test_wise" : ""}
                      onClick={() => { setTestBased("test"); setBreakdownPage(1); setBreakdownSearch(""); }}
                    >
                      Test Wise
                    </section>
                  </section>
                </section>

                <div className="breakdown_search_box">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    placeholder={testBased === "test" ? "Search by test name..." : "Search by chapter name..."}
                    value={breakdownSearch}
                    onChange={(e) => { setBreakdownSearch(e.target.value); setBreakdownPage(1); }}
                  />
                  {breakdownSearch && (
                    <button className="breakdown_search_clear" onClick={() => { setBreakdownSearch(""); setBreakdownPage(1); }}>×</button>
                  )}
                </div>
              </div>

                <table className="teacher_table">
                  <thead>
                    <tr>
                      <th>{testBased === "chapter" ? "Chapter Name" : "Test Name"}</th>
                      {testBased === "test" && <th>Room Code</th>}
                      <th>No.of Attempts</th>
                      <th>Questions Attempted</th>
                      <th>Not Attempted</th>
                      <th>Correct</th>
                      <th>Incorrect</th>
                      <th>Score</th>
                    </tr>
                  </thead>

                  <tbody>
                    {breakdownLoading ? (
                      <tr>
                        <td
                          colSpan={testBased === "test" ? 8 : 7}
                          style={{
                            textAlign: "center",
                            padding: "24px",
                            color: "#612EF7",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          Loading...
                        </td>
                      </tr>
                    ) : pagedBreakdown?.length > 0 ? (
                      pagedBreakdown?.map((item, index) => {
                      const isChapter = testBased === "chapter";
                      const isChapterExpanded = isChapter && !!expandedChapters[index];
                      const chapterName = item?.chapter_name || item?.chapter || item?.test_name || "";
                      const isLoadingSubTopics = !!subTopicsLoading[chapterName];
                      const fetchedSubTopics = subTopicsMap[chapterName];
                      const concepts = isChapter
                        ? fetchedSubTopics !== undefined
                          ? fetchedSubTopics
                          : getMockConcepts(item)
                        : [];

                      return (
                        <Fragment key={index}>
                          <tr
                            className={`chapter_main_row ${isChapterExpanded ? "expanded" : ""} ${
                              (studentAiModalLoading && selectedAiTest === item) ||
                              (testQuestionLoading && selectedQuestionTest === item)
                                ? "is_row_loading"
                                : ""
                            }`}
                            onClick={() => {
                              if (isChapter) {
                                toggleChapter(index, item);
                              } else {
                                openStudentAiSummary(item);
                              }
                            }}
                            style={{ cursor: "pointer" }}
                            title={
                              isChapter
                                ? "Click to view sub-topics"
                                : "Click to view AI summary & question details"
                            }
                          >
                            <td>
                              <div className="chapter_cell_content">
                                {isChapter ? (
                                  <button
                                    type="button"
                                    className="chapter_toggle_btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleChapter(index, item);
                                    }}
                                    title="Toggle Sub-topics"
                                  >
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#612EF7"
                                      strokeWidth="2.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      style={{
                                        transform: isChapterExpanded
                                          ? "rotate(90deg)"
                                          : "rotate(0deg)",
                                        transition: "transform 0.2s ease",
                                      }}
                                    >
                                      <path d="m9 18 6-6-6-6" />
                                    </svg>
                                  </button>
                                ) : (
                                  <span
                                    className="test_row_sparkle"
                                    title="View AI Performance Analysis"
                                  >
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2.1"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
                                      <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
                                      <path d="M12 3v18" />
                                      <path d="M8 9h4" />
                                      <path d="M12 14h4" />
                                    </svg>
                                  </span>
                                )}
                                <span className="chapter_title_text">
                                  {testBased === "test"
                                    ? item?.test_name || item?.chapter_name || "-"
                                    : item?.chapter_name || item?.test_name || "-"}
                                  {studentAiModalLoading && selectedAiTest === item && (
                                    <span
                                      style={{
                                        marginLeft: "8px",
                                        fontSize: "12px",
                                        color: "#612EF7",
                                        fontWeight: "600",
                                      }}
                                    >
                                      (Loading AI Summary...)
                                    </span>
                                  )}
                                </span>
                              </div>
                            </td>
                            {testBased === "test" && (
                              <td>{item?.room_code || "-"}</td>
                            )}
                            <td>{item?.attempts ?? "-"}</td>
                            <td>{item?.attempted_questions ?? "-"}</td>
                            <td>{item?.not_attempted_questions ?? "-"}</td>
                            <td>{item?.correct_answers ?? "-"}</td>
                            <td className="incorrect_count">{item?.incorrect_answers ?? "-"}</td>
                            <td className="score_percentage">
                              {testBased === "test"
                                ? item?.score !== undefined && item?.score !== null
                                  ? item.score
                                  : item?.average_score !== undefined && item?.average_score !== null
                                    ? String(item.average_score).includes("%")
                                      ? item.average_score
                                      : `${item.average_score}%`
                                    : "-"
                                : item?.average_score !== undefined && item?.average_score !== null
                                  ? String(item.average_score).includes("%")
                                    ? item.average_score
                                    : `${item.average_score}%`
                                  : item?.score !== undefined && item?.score !== null
                                    ? String(item.score).includes("%")
                                      ? item.score
                                      : `${item.score}%`
                                    : "-"}
                            </td>
                          </tr>

                          {/* Chapter Sub-topics Accordion */}
                          {isChapter && isChapterExpanded && (
                            isLoadingSubTopics ? (
                              <tr key={`${index}-loading`} className="concept_sub_row">
                                <td colSpan="7" style={{ textAlign: "center", padding: "12px", color: "#612EF7", fontSize: "13px" }}>
                                  Loading sub-topics...
                                </td>
                              </tr>
                            ) : concepts.length === 0 ? (
                              <tr key={`${index}-empty`} className="concept_sub_row">
                                <td colSpan="7" style={{ textAlign: "center", padding: "12px", color: "#64748b", fontSize: "13px" }}>
                                  No sub-topics available for this chapter.
                                </td>
                              </tr>
                            ) : (
                              concepts.map((concept, cIdx) => (
                                <tr
                                  key={`${index}-concept-${cIdx}`}
                                  className="concept_sub_row"
                                >
                                  <td className="concept_name_cell">
                                    {concept?.concept_name || concept?.name || "-"}
                                  </td>
                                  <td>{concept?.attempts ?? "-"}</td>
                                  <td>{concept?.attempted_questions ?? "-"}</td>
                                  <td>{concept?.not_attempted_questions ?? "-"}</td>
                                  <td>{concept?.correct_answers ?? "-"}</td>
                                  <td className="incorrect_count">{concept?.incorrect_answers ?? "-"}</td>
                                  <td className="score_percentage">
                                    {concept?.average_score !== undefined && concept?.average_score !== null
                                      ? String(concept.average_score).includes("%")
                                        ? concept.average_score
                                        : `${concept.average_score}%`
                                      : concept?.score !== undefined && concept?.score !== null
                                        ? concept.score
                                        : "-"}
                                  </td>
                                </tr>
                              ))
                            )
                          )}
                        </Fragment>
                      );
                    })
                  ) : (
                      <tr>
                        <td colSpan={testBased === "test" ? 8 : 7} className="no_data_found_table">
                          {breakdownSearch ? `No results for "${breakdownSearch}"` : "No Data Found"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                {!breakdownLoading && filteredBreakdown.length > BREAKDOWN_PAGE_SIZE && (
                  <div className="breakdown_pagination">
                    <span className="breakdown_page_info">
                      Showing {(breakdownPage - 1) * BREAKDOWN_PAGE_SIZE + 1}–{Math.min(breakdownPage * BREAKDOWN_PAGE_SIZE, filteredBreakdown.length)} of {filteredBreakdown.length}
                    </span>
                    <div className="breakdown_page_btns">
                      <button
                        className="breakdown_page_btn"
                        disabled={breakdownPage === 1}
                        onClick={() => setBreakdownPage((p) => p - 1)}
                      >
                        ‹ Prev
                      </button>
                      {Array.from({ length: totalBreakdownPages }, (_, i) => i + 1)
                        .filter((p) => p === 1 || p === totalBreakdownPages || Math.abs(p - breakdownPage) <= 1)
                        .reduce((acc, p, i, arr) => {
                          if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, i) =>
                          p === "..." ? (
                            <span key={`ellipsis-${i}`} className="breakdown_page_ellipsis">…</span>
                          ) : (
                            <button
                              key={p}
                              className={`breakdown_page_btn ${breakdownPage === p ? "active" : ""}`}
                              onClick={() => setBreakdownPage(p)}
                            >
                              {p}
                            </button>
                          )
                        )}
                      <button
                        className="breakdown_page_btn"
                        disabled={breakdownPage === totalBreakdownPages}
                        onClick={() => setBreakdownPage((p) => p + 1)}
                      >
                        Next ›
                      </button>
                    </div>
                  </div>
                )}

              <section className="teacher_find_btn_container">
                {!showWeakness && (
                  <section
                    className="teacher_find_btn"
                    onClick={() =>
                      getWeakneesAnalysis(newbreakDetails?.summary?.email)
                    }
                  >
                    <img src={assets.ic_search_white} alt="ic_search_white" />
                    <h4>{loading ? "Loading..." : "Find Weakness"}</h4>
                  </section>
                )}
              </section>
              {showWeakness && (
                <section>
                  <section className="weakness_area_wrapper">
                    <h4>Weak Area</h4>
                    <div className="weakness_card">
                      {weaknessDetails?.analysis?.weakness?.map(
                        (item, index) => (
                          <div className="weakness_item" key={index}>
                            <span className="teacher_details_dot"></span>
                            <section className="weakness_content_wrapper">
                              {/* <h4>For CHAPTER:</h4> */}
                              <p>{item}</p>
                            </section>
                          </div>
                        ),
                      )}
                    </div>
                  </section>
                  <section className="tips_area_wrapper">
                    <h4>Tips</h4>
                    <div className="tips_card">
                      {weaknessDetails?.analysis?.tips_to_improve?.map(
                        (item, index) => (
                          <div className="tips_item" key={index}>
                            <span className="teacher_details_dot"></span>
                            <section className="weakness_content_wrapper">
                              {/* <h4>For CHAPTER:</h4> */}
                              <p>{item}</p>
                            </section>
                          </div>
                        ),
                      )}
                    </div>
                  </section>
                  <section
                    className="send_weakness_btn_wrapper"
                    onClick={() =>
                      teacherSendTipsToStudents(newbreakDetails?.summary?.email)
                    }
                  >
                    <img src={assets.ic_send} alt="ic_send" />
                    <h4>{loading ? "Loading..." : "Send Weakness Tips"}</h4>
                  </section>
                </section>
              )}
            </div>
          </div>
        </div>
      )}

      <StudentAiSummaryModal
        open={studentAiModalOpen}
        onClose={() => setStudentAiModalOpen(false)}
        data={studentAiModalData}
        loading={studentAiModalLoading}
        selectedTest={selectedAiTest}
        studentEmail={newbreakDetails?.summary?.email || selectedStudent?.email}
        onViewQuestions={handleViewQuestionDetails}
        questionsLoading={testQuestionLoading}
      />

      <TestQuestionDetailsModal
        open={testQuestionModalOpen}
        onClose={() => setTestQuestionModalOpen(false)}
        onBack={() => {
          setTestQuestionModalOpen(false);
          setStudentAiModalOpen(true);
        }}
        data={testQuestionDetails}
        loading={testQuestionLoading}
        selectedTest={selectedQuestionTest}
      />
    </div>
  );
};
